import type { FC } from 'react';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { InfoIcon } from '@/assets';
import type { TourFeature } from '@/features/tutorial/types/tutorial';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const FEATURE_ICON_SIZE = 24;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: { gap: 12 },
    features: { gap: 8 },
    feature: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    featureLabel: { flex: 1 },
    hint: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      padding: 12,
      borderRadius: 12,
      backgroundColor: theme.colors.surfaceContainerHigh
    },
    hintText: { flex: 1, color: theme.colors.onSurfaceVariant }
  });

type Props = {
  title: string;
  summary: string;
  features: TourFeature[];
  hint?: string;
};

export const PageContent: FC<Props> = ({ title, summary, features, hint }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View style={styles.container}>
      <Text variant="headlineSmall">{title}</Text>
      <Text variant="bodyLarge">{summary}</Text>
      <View style={styles.features}>
        {features.map(({ icon: Icon, label }, index) => (
          <View key={index} style={styles.feature}>
            <Icon
              fill={theme.colors.primary}
              color={theme.colors.primary}
              width={FEATURE_ICON_SIZE}
              height={FEATURE_ICON_SIZE}
            />
            <Text variant="bodyLarge" style={styles.featureLabel}>
              {label}
            </Text>
          </View>
        ))}
      </View>
      {hint ? (
        <View style={styles.hint}>
          <InfoIcon
            fill={theme.colors.onSurfaceVariant}
            width={20}
            height={20}
          />
          <Text variant="bodyMedium" style={styles.hintText}>
            {hint}
          </Text>
        </View>
      ) : undefined}
    </View>
  );
};
